/**
 * @file wechatPay.js
 * @description 微信 JS-SDK 加载与 JSAPI 调起支付
 * @module shared/utils/wechatPay
 */

const WX_JSSDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'

/** JSAPI 调起支付进行中（防双次 getBrandWCPayRequest） */
let wxPayInvokeInFlight = false

/**
 * @param {boolean} inFlight
 */
function setWxPayInvokeInFlight(inFlight) {
  wxPayInvokeInFlight = inFlight
}

/**
 * @typedef {Object} WxConfigParams
 * @property {string} appId
 * @property {string} timestamp
 * @property {string} nonceStr
 * @property {string} signature
 */

/**
 * @typedef {Object} WxPayParams
 * @property {string} appId
 * @property {string} timeStamp
 * @property {string} nonceStr
 * @property {string} package
 * @property {string} signType
 * @property {string} paySign
 */

/**
 * 动态加载微信 JSSDK
 * @returns {Promise<void>}
 */
export function loadWeChatJSSDK() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('非浏览器环境'))
  }
  if (window.wx) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${WX_JSSDK_URL}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('微信 JSSDK 加载失败')))
      if (window.wx) resolve()
      return
    }
    const script = document.createElement('script')
    script.src = WX_JSSDK_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('微信 JSSDK 加载失败'))
    document.head.appendChild(script)
  })
}

/**
 * 初始化 wx.config
 * @param {WxConfigParams} config
 * @param {string[]} [jsApiList=['chooseWXPay']]
 * @returns {Promise<void>}
 */
export function initWeChatConfig(config, jsApiList = ['chooseWXPay']) {
  return loadWeChatJSSDK().then(
    () =>
      new Promise((resolve, reject) => {
        if (!window.wx) {
          reject(new Error('微信 JSSDK 不可用'))
          return
        }
        window.wx.config({
          debug: false,
          appId: config.appId,
          timestamp: config.timestamp,
          nonceStr: config.nonceStr,
          signature: config.signature,
          jsApiList,
        })
        window.wx.ready(() => resolve())
        window.wx.error((err) => reject(err || new Error('wx.config 失败')))
      })
  )
}

/**
 * 等待微信内置浏览器注入 WeixinJSBridge
 * @returns {Promise<void>}
 */
function waitForWeixinJSBridge() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('非浏览器环境'))
  }
  if (typeof window.WeixinJSBridge !== 'undefined') {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      document.removeEventListener('WeixinJSBridgeReady', onReady, false)
      reject(new Error('请在微信内打开后再支付'))
    }, 8000)
    function onReady() {
      clearTimeout(timer)
      document.removeEventListener('WeixinJSBridgeReady', onReady, false)
      resolve()
    }
    document.addEventListener('WeixinJSBridgeReady', onReady, false)
  })
}

/**
 * 校验并规范化 JSAPI 调起参数
 * @param {WxPayParams} payParams
 * @returns {Record<string, string>}
 */
function normalizeWxPayParams(payParams) {
  const appId = payParams?.appId != null ? String(payParams.appId).trim() : ''
  const timeStamp = payParams?.timeStamp != null ? String(payParams.timeStamp).trim() : ''
  const nonceStr = payParams?.nonceStr != null ? String(payParams.nonceStr).trim() : ''
  const pkg = payParams?.package != null ? String(payParams.package).trim() : ''
  const signType = payParams?.signType != null ? String(payParams.signType).trim() : 'RSA'
  const paySign = payParams?.paySign != null ? String(payParams.paySign).trim() : ''

  if (!appId) throw new Error('支付参数缺少 appId，请重试')
  if (!timeStamp) throw new Error('支付参数缺少 timeStamp，请重试')
  if (!nonceStr) throw new Error('支付参数缺少 nonceStr，请重试')
  if (!pkg) throw new Error('支付参数缺少 package，请重试')
  if (!paySign) throw new Error('支付参数缺少 paySign，请重试')

  return {
    appId,
    timeStamp,
    nonceStr,
    package: pkg,
    signType: signType || 'RSA',
    paySign,
  }
}

/**
 * 公众号内 H5：WeixinJSBridge.getBrandWCPayRequest（须传 appId）
 * @param {Record<string, string>} bridgeParams
 * @returns {Promise<void>}
 */
function invokeBrandWCPayRequest(bridgeParams) {
  return new Promise((resolve, reject) => {
    const finish = (fn, arg) => {
      setWxPayInvokeInFlight(false)
      fn(arg)
    }
    window.WeixinJSBridge.invoke('getBrandWCPayRequest', bridgeParams, (res) => {
      const msg = res && res.err_msg ? String(res.err_msg) : ''
      if (msg === 'get_brand_wcpay_request:ok') {
        finish(resolve)
        return
      }
      if (msg === 'get_brand_wcpay_request:cancel') {
        finish(reject, new Error('用户取消支付'))
        return
      }
      finish(reject, new Error(msg || '支付失败'))
    })
  })
}

/**
 * JSSDK 兜底：须已 wx.config；仅在没有 WeixinJSBridge 时尝试
 * @param {Record<string, string>} bridgeParams
 * @returns {Promise<void>}
 */
function invokeChooseWXPayFallback(bridgeParams) {
  return loadWeChatJSSDK().then(
    () =>
      new Promise((resolve, reject) => {
        if (!window.wx) {
          setWxPayInvokeInFlight(false)
          reject(new Error('微信 JSSDK 不可用'))
          return
        }
        window.wx.chooseWXPay({
          appId: bridgeParams.appId,
          timestamp: bridgeParams.timeStamp,
          nonceStr: bridgeParams.nonceStr,
          package: bridgeParams.package,
          signType: bridgeParams.signType,
          paySign: bridgeParams.paySign,
          success: () => {
            setWxPayInvokeInFlight(false)
            resolve()
          },
          fail: (err) => {
            setWxPayInvokeInFlight(false)
            reject(err || new Error('支付失败'))
          },
          cancel: () => {
            setWxPayInvokeInFlight(false)
            reject(new Error('用户取消支付'))
          },
        })
      })
  )
}

/**
 * 调起微信 JSAPI 支付（公众号内优先 WeixinJSBridge + appId）
 * @param {WxPayParams} payParams
 * @returns {Promise<void>}
 */
export function invokeWeChatPay(payParams) {
  if (wxPayInvokeInFlight) {
    console.info('[tip-pay] invokeWeChatPay blocked: inFlight')
    return Promise.reject(new Error('支付进行中，请勿重复操作'))
  }
  wxPayInvokeInFlight = true
  console.info('[tip-pay] invokeWeChatPay start')

  let bridgeParams
  try {
    bridgeParams = normalizeWxPayParams(payParams)
  } catch (err) {
    setWxPayInvokeInFlight(false)
    throw err
  }

  return waitForWeixinJSBridge()
    .then(() => invokeBrandWCPayRequest(bridgeParams))
    .catch((bridgeErr) => {
      if (typeof window !== 'undefined' && typeof window.WeixinJSBridge !== 'undefined') {
        setWxPayInvokeInFlight(false)
        throw bridgeErr
      }
      return invokeChooseWXPayFallback(bridgeParams)
    })
    .catch((err) => {
      setWxPayInvokeInFlight(false)
      throw err
    })
}
